import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Where a card's arrow asks the tab to land: an element's id. An arrow without one leaves the scroll where it was. */
export type ScrollTarget = string;

/** Room left above what it lands on, so a card is not pressed against the top edge. */
const LANDING_GAP = 24;

/** Eases out rather than the browser's own smooth scroll, which stops dead at the end. Returns a stop. */
const glideTo = (top: number): (() => void) => {
  const scroller = document.scrollingElement ?? document.documentElement;
  const from = scroller.scrollTop;
  const to = Math.min(scroller.scrollHeight - scroller.clientHeight, Math.max(0, top));
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    scroller.scrollTop = to;
    return () => undefined;
  }
  const duration = 700;
  const start = performance.now();
  let frame = 0;
  // The reader's own scrolling wins: a glide that kept going would snap the page back from under them.
  const stop = (): void => {
    cancelAnimationFrame(frame);
    ['wheel', 'touchstart', 'keydown'].forEach(name => window.removeEventListener(name, stop));
  };
  ['wheel', 'touchstart', 'keydown'].forEach(name => window.addEventListener(name, stop, { passive: true }));
  const step = (now: number): void => {
    const progress = Math.min(1, (now - start) / duration);
    // Cubic ease-out: quick to leave, slowing gently into place.
    scroller.scrollTop = from + (to - from) * (1 - (1 - progress) ** 3);
    if (progress < 1) frame = requestAnimationFrame(step);
    else stop();
  };
  frame = requestAnimationFrame(step);
  return stop;
};

/**
 * Arriving from a card's arrow: glide to the part of the tab it pointed at, once the tab has its height. A tab
 * switch replaces the entry and keeps the scroll it came with, so without this the page opens halfway down.
 */
export const useArrivalScroll = (ready: boolean): void => {
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const target = (state as { scrollTo?: ScrollTarget } | null)?.scrollTo;
  const landed = useRef<ScrollTarget | null>(null);
  const stopGlide = useRef<() => void>(() => undefined);
  // On leaving the tab only: the state change after landing re-runs the effect below, mid-glide.
  useEffect(() => () => stopGlide.current(), []);
  useEffect(() => {
    if (!ready || !target || landed.current === target) return undefined;
    // After the tab's 180ms fade, so the page settles first and then moves, one motion at a time.
    const timer = window.setTimeout(() => {
      const element = document.getElementById(target);
      if (!element) return;
      landed.current = target;
      // Off the entry once it has landed: tab switches carry the state on, and a plain click must not glide.
      navigate(`${pathname}${search}`, { replace: true, state: { ...(state as object), scrollTo: undefined } });
      const scroller = document.scrollingElement ?? document.documentElement;
      const box = element.getBoundingClientRect();
      // Already whole on screen, so there is nothing to bring into view.
      if (box.top >= 0 && box.bottom <= window.innerHeight) return;
      stopGlide.current = glideTo(scroller.scrollTop + box.top - LANDING_GAP);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [ready, target, navigate, pathname, search, state]);
};
